import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
// --- Added BiSearch ---
import { BiDollarCircle, BiSearch } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import LoadingSpinner from "../common/LoadingSpinner";

const SuperAdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // --- Added ---
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 15;

  // --- Changed ---
  // Now accepts a query string to send to the API
  const fetchTransactions = async (page = 1, query = "") => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/super-admin/transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          // --- Changed ---
          // Send page, pageSize, and search query to the API
          params: { page, pageSize, search: query },
        }
      );
      setTransactions(response.data.data);
      setTotalPages(response.data.metadata.totalPages);
      setCurrentPage(response.data.metadata.currentPage);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError("Failed to load transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- Changed ---
  // This effect now runs when *either* the page or the search query changes.
  useEffect(() => {
    // We use a debounce to avoid sending an API request on every keystroke
    const timer = setTimeout(() => {
      fetchTransactions(currentPage, searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer); // Cleanup timer
  }, [currentPage, searchQuery]);

  // --- Added ---
  // This effect resets the page to 1 whenever the search query changes.
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery]);
  // --- End Added ---

  // --- Removed handlePageChange (it was unused) ---

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleString() : "N/A";
  const formatCurrency = (amount) =>
    amount != null ? `$${parseFloat(amount).toFixed(2)}` : "N/A";

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "succeeded":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-500";
    }
  };

  if (loading)
    return (
      <div className="p-10 flex justify-center">
        <LoadingSpinner />
      </div>
    );

  if (error)
    return (
      <div className="p-10 text-center text-xl text-red-600 bg-red-50 rounded-lg">
        {error}
      </div>
    );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* --- Changed --- Header now includes search bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
          <BiDollarCircle className="w-7 h-7 mr-2 text-green-600" /> All
          Transactions
        </h1>
        {/* --- Added Search Input --- */}
        <div className="relative w-full sm:w-64">
          <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                  Organization
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                  User
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600 uppercase text-xs">
                  Amount
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                  Date
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 uppercase text-xs">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {transactions.length > 0 ? (
                transactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >

                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {tx.organization_id ? (
                        <Link
                          to={`/organizations/view/${tx.organization_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {tx.organization_name}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {tx.user_id ? (
                        <Link
                          to={`/super-admin/users/${tx.user_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {tx.user_name}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {formatCurrency(tx.amount)}
                    </td>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {formatDate(tx.created_at)}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getStatusClass(
                          tx.status
                        )}`}
                      >
                        {tx.status || "Unknown"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📱 Mobile Card Layout */}
      <div className="md:hidden space-y-4">
        {transactions.length > 0 ? (
          transactions.map((tx) => (
            <div
              key={tx.id}
              className="bg-white rounded-lg shadow-sm p-4 border border-gray-100"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">
                  {formatDate(tx.created_at)}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${getStatusClass(
                    tx.status
                  )}`}
                >
                  {tx.status || "Unknown"}
                </span>
              </div>

              <div className="space-y-1 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">
                    Organization:
                  </span>{" "}
                  {tx.organization_id ? (
                    <Link
                      to={`/organizations/view/${tx.organization_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {tx.organization_name}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </div>


                <div>
                  <span className="font-semibold text-gray-700">User:</span>{" "}
                  {tx.user_id ? (
                    <Link
                      to={`/super-admin/users/${tx.user_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {tx.user_name}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </div>

                <div className="flex justify-between mt-2 text-sm">
                  <span className="text-gray-700 font-medium">
                    {formatCurrency(tx.amount)}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 text-sm">
            No transactions found.
          </p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SuperAdminTransactions;